import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCall } from "@/context/CallContext";
import { toast } from "sonner";
import { UserPlus, X, AtSign, Users, Wallet, Video, ShieldCheck } from "lucide-react";
import { dollars } from "@/lib/giftIcons";
import { api } from "@/lib/api";

// Horizontal strip of recent matches to pick a guest from (verified matches only are invitable).
function MatchPicker({ matches, selected, onPick }) {
  if (matches === null) return <p data-testid="match-picker-loading" className="text-[11px] text-text-muted mb-3">Loading your matches…</p>;
  if (!matches.length) return null;
  return (
    <div className="mb-3">
      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted mb-2">Recent matches</p>
      <div data-testid="match-picker" className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {matches.slice(0, 12).map((u) => {
          const ok = !!u.age_verified, active = selected === u.id;
          return (
            <button type="button" key={u.id} data-testid={`match-pick-${u.id}`} onClick={() => ok && onPick(u)} disabled={!ok}
              title={ok ? u.address : `${u.display_name} hasn't completed ID verification`}
              className={`shrink-0 w-16 flex flex-col items-center gap-1.5 text-center transition-opacity ${ok ? "" : "opacity-40 cursor-not-allowed"}`}>
              <span className={`relative w-12 h-12 rounded-full border-2 overflow-hidden bg-black/40 transition-colors ${active ? "border-gold" : "border-white/15"}`}>
                {u.photo_url ? <img src={u.photo_url} alt="" className="w-full h-full object-cover" />
                  : <span className="w-full h-full flex items-center justify-center text-sm text-gold font-display">{(u.display_name || "?")[0]}</span>}
                {u.online && <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border border-black" />}
              </span>
              <span className={`text-[10px] truncate w-full flex items-center justify-center gap-0.5 ${active ? "text-gold" : "text-text-main"}`}>
                {u.display_name}{ok && <ShieldCheck size={9} className="text-gold shrink-0" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// "Add Person" button + invite panel shown to the paying member during a live private session.
export function AddPersonButton({ rate }) {
  const { inviteToSession, guests } = useCall();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [billing, setBilling] = useState("split");
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null);
  const hasGuest = Object.keys(guests).length > 0;
  useEffect(() => {
    if (!open || matches) return;
    api.get("/matches?sort=active").then(({ data }) => setMatches(data.map((m) => m.user).filter((u) => u.handle))).catch(() => setMatches([]));
  }, [open, matches]);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const data = await inviteToSession(address.trim(), billing);
      toast.success(`Asking ${"the creator"} to approve ${data.invitee.name}…`); setOpen(false); setAddress("");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send invite"); } finally { setBusy(false); }
  };
  const selected = matches?.find((u) => u.handle === address.trim().replace(/@velluto$/i, "").replace(/^@/, ""));
  return (
    <>
      <button data-testid="call-add-person" onClick={() => setOpen((o) => !o)} disabled={hasGuest} title={hasGuest ? "Session already has three people" : "Invite a third person"}
        className="w-16 h-16 rounded-full border border-gold/50 text-gold hover:bg-gold/10 transition-colors flex items-center justify-center disabled:opacity-40">
        <UserPlus size={22} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} onSubmit={send}
            data-testid="invite-panel" className="absolute bottom-28 sm:bottom-36 left-1/2 -translate-x-1/2 w-[92vw] max-w-md max-h-[62dvh] overflow-y-auto overscroll-contain rounded-3xl border border-gold/40 bg-surface/95 backdrop-blur-xl p-5 text-left shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><Users size={14} /> Invite a third person</p>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-text-muted hover:text-text-main"><X size={14} /></button>
            </div>
            <MatchPicker matches={matches} selected={selected?.id} onPick={(u) => setAddress(u.address || u.handle)} />
            <div className="flex items-center gap-2 rounded-full border border-white/15 bg-black/30 pl-4 pr-2 py-2 focus-within:border-gold/50">
              <AtSign size={14} className="text-gold" />
              <input data-testid="invite-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={matches?.length ? "or type name@velluto" : "name@velluto"} autoFocus={!matches?.length}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {[["inviter", "Pay for both", `You cover 2 × ${dollars(rate)}/min`], ["split", "Split payment", `They pay their own ${dollars(rate)}/min`]].map(([v, l, d]) => (
                <button type="button" key={v} data-testid={`invite-billing-${v}`} onClick={() => setBilling(v)}
                  className={`rounded-2xl border px-3 py-3 text-left transition-colors ${billing === v ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/30"}`}>
                  <p className="text-xs uppercase tracking-[0.12em] flex items-center gap-1.5"><Wallet size={12} className={billing === v ? "text-gold" : "text-text-muted"} /> {l}</p>
                  <p className="text-[11px] text-text-muted mt-1">{d}</p>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-text-muted mt-3">
              {billing === "inviter" ? "If your wallet runs out, the session ends for everyone." : "If their wallet runs out they're dropped; your session continues."}
            </p>
            <button data-testid="invite-send" disabled={busy || !address.trim()} className="mt-4 w-full py-3 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 disabled:opacity-40">
              {busy ? "Sending…" : "Send invite"}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </>
  );
}

// Creator consent card: approve or refuse a third person before the invite reaches them.
export function ConsentCard() {
  const { consent, decideConsent } = useCall();
  if (!consent) return null;
  const c = consent;
  return (
    <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="consent-card"
      className="absolute top-20 left-1/2 -translate-x-1/2 z-20 w-[92vw] max-w-md rounded-3xl border border-gold/50 bg-surface/95 backdrop-blur-xl p-5 shadow-2xl text-left">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><Users size={14} /> Add a third person?</p>
      <div className="flex items-center gap-3 mt-3">
        {c.invitee?.photo_url && <img src={c.invitee.photo_url} alt="" className="w-11 h-11 rounded-full object-cover border border-gold/40" />}
        <p className="text-sm"><span className="text-text-main">{c.inviter?.name}</span> wants to bring <span className="text-gold">{c.invitee?.name}</span>{c.invitee?.age_verified ? " (ID-verified)" : ""} into this session.</p>
      </div>
      <p data-testid="consent-terms" className="text-xs text-text-muted mt-3">
        {c.billing === "inviter" ? `${c.inviter?.name} pays for both connections` : "Each of them pays their own connection"} — you earn on both, {dollars(c.rate)}/min each.
      </p>
      <div className="flex gap-2 mt-4">
        <button data-testid="consent-refuse" onClick={() => decideConsent(c, false)} className="flex-1 py-3 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.15em] hover:text-text-main">Refuse</button>
        <button data-testid="consent-approve" onClick={() => decideConsent(c, true)} className="flex-1 py-3 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.15em] hover:bg-gold/90">Approve</button>
      </div>
    </motion.div>
  );
}

// Incoming "Invite to Join" card for the invited member (rendered globally).
export function IncomingInvite() {
  const { invite, joinAsGuest, declineInvite } = useCall();
  if (!invite) return null;
  const inv = invite;
  return (
    <motion.div initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="incoming-invite"
      className="fixed top-20 left-1/2 -translate-x-1/2 z-[9998] w-[92vw] max-w-md rounded-3xl border border-gold/50 bg-surface/95 backdrop-blur-xl p-5 shadow-2xl">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><Video size={14} /> Invite to join a CamChat</p>
      <div className="flex items-center gap-3 mt-3">
        {inv.inviter?.photo_url && <img src={inv.inviter.photo_url} alt="" className="w-11 h-11 rounded-full object-cover border border-gold/40" />}
        <p className="text-sm"><span className="text-text-main">{inv.inviter?.name}</span> invited you into a live session with <span className="text-gold">{inv.creator?.name}</span>.</p>
      </div>
      <p data-testid="invite-terms" className="text-xs text-text-muted mt-3">
        {inv.billing === "inviter" ? `${inv.inviter?.name} is paying for your connection — it's on them.` : `Split payment: you'll be charged ${dollars(inv.rate)} per minute from your wallet.`}
      </p>
      <div className="flex gap-2 mt-4">
        <button data-testid="invite-decline" onClick={() => declineInvite(inv)} className="flex-1 py-3 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.15em] hover:text-text-main">Decline</button>
        <button data-testid="invite-join" onClick={() => joinAsGuest(inv)} className="flex-1 py-3 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.15em] hover:bg-gold/90">Join session</button>
      </div>
    </motion.div>
  );
}
